/**
 * Build the 4-level workloads tree from raw API responses.
 *
 *     Global > Service > Pool > Pod
 *
 * The Global root is virtual — it does not correspond to any DB row but
 * acts as the entry point for cross-service controls (Envoy data plane,
 * egress IPs, ...). Its `stats` are aggregated across every service.
 *
 * The previous 5-level layout interposed a "GPU group" virtual row
 * between Service and Pool, but the Pool *is already* a same-shape
 * group by definition (`accelerators` / `cpus` are pool-level
 * resource selectors). Collapsing the redundant level keeps the
 * keyboard / click depth shallower and surfaces shape information
 * directly on the Pool row label.
 */
import { resolvePoolLabel } from "@/api/local/labels";
import type { Pool, Replica, ReplicaStatus, Service } from "@/api/types";

export type NodeType = "global" | "service" | "pool" | "pod";

/** Stable slug for the resource shape, used by cost grouping & labels. */
export function poolShapeSlug(pool: Pool): string {
  if (pool.accelerators) return pool.accelerators;
  if (pool.cpus) return `cpu:${pool.cpus}`;
  return "any";
}

/**
 * Human-facing rendering of a resource-shape slug.
 *
 *   "H100:1"  -> "H100 × 1"
 *   "cpu:8+"  -> "8+ CPU"
 *   "any"     -> "Default shape"
 */
export function humanShapeLabel(slug: string): string {
  if (slug === "any") return "Default shape";
  if (slug.startsWith("cpu:")) {
    const cpus = slug.slice("cpu:".length);
    return cpus ? `${cpus} CPU` : "CPU";
  }
  const colon = slug.lastIndexOf(":");
  if (colon === -1) return slug;
  const accel = slug.slice(0, colon);
  const count = slug.slice(colon + 1);
  return count ? `${accel}×${count}` : accel;
}

/**
 * A "draft" pool is one the WebUI inserted via "+ pool" but the
 * operator hasn't yet configured on the Spec tab. Detection keys off
 * the resource shape: a Pool with neither `accelerators` nor `cpus`
 * has nothing meaningful to render or provision (the reconciler also
 * treats it as inert when the provider-config inheritance chain
 * resolves to zero candidate placements). We surface this state
 * explicitly in tree rows / breadcrumbs so the UI doesn't pretend
 * the operator picked H100×1 when in fact they picked nothing.
 */
export function isPoolDraft(pool: Pool): boolean {
  return !pool.accelerators && !pool.cpus;
}

const CLOUD_DISPLAY: Record<string, string> = {
  aws: "AWS",
  gcp: "GCP",
  azure: "Azure",
  runpod: "RunPod",
  lambda: "Lambda",
  fluidstack: "FluidStack",
  cudo: "Cudo",
  paperspace: "Paperspace",
  kubernetes: "Kubernetes",
};

/** Human-facing rendering of a cloud slug. */
export function humanCloudLabel(cloud: string): string {
  return CLOUD_DISPLAY[cloud.toLowerCase()] ?? cloud;
}

/**
 * Title for a Pool row in the tree.
 *
 * The title is the operator-editable display name (``Pool.display_name``,
 * falling back to the slug ``sky_service_name``) so the rename pencil
 * is reflected in the tree. The resource shape (H100×1 / 8+ CPU / ...)
 * and shape-adjacent flags (reservation) move to the sub-line — see
 * ``poolDisplaySublabel`` — because the same-shape group identity
 * has nowhere else to live on the Pool row.
 */
export function poolDisplayLabel(pool: Pool): string {
  return resolvePoolLabel(pool);
}

/** Tagged sublabel chip kinds rendered by the tree row. */
export type PoolSublabelChip = "cascade" | "slo";
export type PodSublabelChip = "ws-only" | "draining" | "shutting-down";

/**
 * Sub-line under a Pool row title. Returns both the plain text (so
 * existing renderers keep working) and a structured list of chips so
 * the tree can render dedicated pills for the cascade / SLO badges.
 *
 * Priority is rendered by the tree as a dedicated column (`P{priority}`
 * Pill) so we no longer prepend it here — that would just be visual
 * duplication of the column.
 */
export function poolDisplaySublabel(pool: Pool): {
  text: string;
  chips: PoolSublabelChip[];
} {
  // Cascade / SLO chips are populated by `decoratePoolNode` once the
  // tree has been built (because they need cross-service data the
  // pure pool row can't see). We expose the chip list so the WebUI
  // can stitch them in without re-parsing the sublabel text. There is
  // no per-pool placement count here anymore — the candidate set is
  // resolved from the Provider config inheritance chain at render
  // time and surfaced on the Pool's Spec / Dashboard tabs.
  //
  // The text portion carries the resource shape that used to live on
  // the title — moved here so the title can carry the editable name.
  const parts: string[] = [];
  parts.push(isPoolDraft(pool) ? "(unconfigured)" : humanShapeLabel(poolShapeSlug(pool)));
  if (pool.reservation_id != null) parts.push("reserved");
  return { text: parts.join(" · "), chips: [] };
}

/** Sublabel chips for a pod row, computed from replica state. */
export function podSublabelChips(replica: Replica): PodSublabelChip[] {
  const chips: PodSublabelChip[] = [];
  if (replica.status === "draining_long_lived") chips.push("ws-only");
  if (replica.status === "draining") chips.push("draining");
  if (replica.status === "shutting_down") chips.push("shutting-down");
  return chips;
}

export interface TreeStats {
  ready: number;
  pending: number;
  failed: number;
  /**
   * Replicas the controller is actively tearing down (`shutting_down`).
   * Tracked separately from `total` so that the cap math can subtract
   * them out — a replica whose SkyServe `down` is already in flight no
   * longer consumes capacity in any meaningful sense, so flagging it as
   * "over cap" misled operators during routine drains.
   */
  shutting: number;
  /** Every replica row, including drain stages and `shutting_down`. */
  total: number;
  /**
   * Sum of known per-replica hourly rates (USD) under this node.
   * Only meaningful when `hourlyCostUnknown === false` — when any
   * billable replica in the subtree has an unknown rate this number
   * is a *partial* sum and the UI must render `—` instead of it.
   */
  hourlyCost: number;
  /**
   * `true` when at least one billable replica under this node has
   * `current_hourly_cost_usd === null` (the SkyPilot catalog never
   * resolved a rate for its SKU). The renderer collapses such nodes
   * to `—` so operators don't read the partial sum as the system's
   * actual burn rate.
   *
   * "Billable" means the replica is in a state that accrues cost on
   * the cloud side — `ready`, `draining*`, or `shutting_down`. We
   * deliberately exclude `pending` / `provisioning` / `failed`:
   * those don't have an open `replica_runs` row yet (or have a
   * closed one) and rendering `—` for a brand-new pool that just
   * spun up a `pending` replica would be more confusing than
   * showing the empty-pool "$0.00".
   */
  hourlyCostUnknown: boolean;
  desired: number;
}

/**
 * Where a replica is running, surfaced inline on the tree's pod row.
 * Both fields are NULL during early provisioning before SkyPilot has
 * reported a `launched_resources` view; the row falls back to a single
 * "—" in that state.
 */
export interface PodLocation {
  cloud: string | null;
  region: string | null;
}

export interface TreeNode {
  key: string;
  /** Indentation level: 0 = global, 1 = service, 2 = pool, 3 = pod. */
  level: 0 | 1 | 2 | 3;
  type: NodeType;
  /**
   * DB id when applicable (null for the virtual global row).
   *
   * For `type === "pod"` rows this is the `pods.id` when the controller
   * has surfaced a Pod for this replica, otherwise the parent
   * `replicas.id` so the row can still be focused via the replica
   * fallback path in `Workloads.tsx`.
   */
  id: number | null;
  label: string;
  /** Optional secondary line shown under the label. */
  sublabel?: string;
  /** Optional pill chips rendered next to the sublabel. */
  poolChips?: PoolSublabelChip[];
  podChips?: PodSublabelChip[];
  service?: Service;
  pool?: Pool;
  replica?: Replica;
  podIndex?: number;
  podRole?: string;
  podInternalIp?: string | null;
  podExternalIp?: string | null;
  /**
   * Cloud/region of this pod's replica. Rendered as a Pill + muted text
   * on the row; only populated for pod-type nodes.
   */
  podLocation?: PodLocation;
  /**
   * Aggregated cloud labels for a pool row — the distinct set of clouds
   * across the pool's replicas (e.g. ["aws", "gcp"]). Empty array when
   * no replicas have a cloud reported yet. Rendered as a row of Pills.
   */
  poolClouds?: string[];
  stats: TreeStats;
  children: TreeNode[];
}

/**
 * Pluggable display-label resolvers. The Workloads page passes a
 * resolver backed by the editable labels store so renames flow
 * through here without touching internal ids/slugs.
 */
export interface TreeLabelResolvers {
  service: (svc: Service) => string;
}

export const DEFAULT_LABELS: TreeLabelResolvers = {
  service: (svc) => svc.name,
};

/** Stable key used by the virtual global root row. */
export const GLOBAL_TREE_KEY = "global";

const PROVISIONING: ReplicaStatus[] = ["pending", "provisioning"];
const FAILED: ReplicaStatus[] = ["failed", "not_ready"];

function emptyStats(): TreeStats {
  return {
    ready: 0,
    pending: 0,
    failed: 0,
    shutting: 0,
    total: 0,
    hourlyCost: 0,
    hourlyCostUnknown: false,
    desired: 0,
  };
}

function addStats(into: TreeStats, from: TreeStats) {
  into.ready += from.ready;
  into.pending += from.pending;
  into.failed += from.failed;
  into.shutting += from.shutting;
  into.total += from.total;
  into.hourlyCost += from.hourlyCost;
  into.hourlyCostUnknown = into.hourlyCostUnknown || from.hourlyCostUnknown;
  into.desired += from.desired;
}

/**
 * Replica states that should be billing on the cloud side. Used as
 * the gate for "the unknown rate of this replica taints the parent
 * subtree's $/HOUR" — `pending` / `failed` etc. have no open
 * `replica_runs` row yet (or have a closed one) so a missing rate on
 * those is the expected zero state, not an error.
 */
const BILLABLE: ReplicaStatus[] = [
  "ready",
  "draining",
  "draining_long_lived",
  "shutting_down",
];

function isBillable(replica: Replica): boolean {
  return BILLABLE.includes(replica.status);
}

/**
 * Per-pod share of a replica's measured rate.
 *
 * An even split across the replica's pods. For a multi-node replica
 * (TP/PP) the head and workers can be different SKUs, so this is an
 * apportionment, not a measurement — the ledger prices the replica as
 * a unit because that is the granularity SkyPilot bills at. Shown
 * anyway: "this pod is roughly a third of $6.27" tells an operator
 * more than a dash does, and the row is explicitly labelled as a
 * share in its tooltip.
 */
function replicaPodHourlyCost(replica: Replica): number {
  const total = replica.current_hourly_cost_usd;
  if (typeof total !== "number") return 0;
  const podCount = Math.max(1, replica.pods.length || 1);
  return total / podCount;
}

function poolStats(pool: Pool, replicas: Replica[]): TreeStats {
  const stats = emptyStats();
  stats.desired = pool.max_replicas;
  for (const r of replicas) {
    stats.total += 1;
    if (r.status === "ready") stats.ready += 1;
    else if (PROVISIONING.includes(r.status)) stats.pending += 1;
    else if (FAILED.includes(r.status)) stats.failed += 1;
    else if (r.status === "shutting_down") stats.shutting += 1;
    // Measured burn rate: sum of currently-open
    // replica_runs.hourly_cost (exposed as
    // Replica.current_hourly_cost_usd). Matches the rate the /cost
    // ledger integrates instead of recomputing ready * cap, which
    // drifts when the operator edits max_price_per_hour mid-run.
    //
    // When a billable replica's rate is `null` (catalog couldn't
    // price its SKU), we flip ``hourlyCostUnknown`` so the renderer
    // collapses to `—` rather than show a partial sum.
    const v = r.current_hourly_cost_usd;
    if (typeof v === "number") {
      stats.hourlyCost += v;
    } else if (isBillable(r)) {
      stats.hourlyCostUnknown = true;
    }
  }
  return stats;
}

export function buildTree(
  services: Service[],
  poolsByService: Map<number, Pool[]>,
  replicas: Replica[],
  labels: TreeLabelResolvers = DEFAULT_LABELS,
): TreeNode[] {
  const replicasByPool = new Map<number, Replica[]>();
  for (const r of replicas) {
    const list = replicasByPool.get(r.pool_id) ?? [];
    list.push(r);
    replicasByPool.set(r.pool_id, list);
  }

  const serviceNodes: TreeNode[] = services.map<TreeNode>((service) => {
    const servicePools = (poolsByService.get(service.id) ?? [])
      .slice()
      .sort((a, b) => a.priority - b.priority);

    const serviceStats = emptyStats();
    const poolNodes: TreeNode[] = [];

    for (const pool of servicePools) {
      const poolReplicas = replicasByPool.get(pool.id) ?? [];
      const ps = poolStats(pool, poolReplicas);
      addStats(serviceStats, ps);

      const podNodes: TreeNode[] = [];
      for (const replica of poolReplicas) {
        const pods = replica.pods.length > 0 ? replica.pods : [null];
        for (const pod of pods) {
          const isReady = replica.status === "ready";
          const podStat: TreeStats = {
            ready: isReady ? 1 : 0,
            pending: PROVISIONING.includes(replica.status) ? 1 : 0,
            failed: FAILED.includes(replica.status) ? 1 : 0,
            shutting: replica.status === "shutting_down" ? 1 : 0,
            total: 1,
            hourlyCost: replicaPodHourlyCost(replica),
            // Flag the pod row's stats as "rate unknown" only when
            // the replica is in a billable state — for `pending` /
            // `failed` we leave the pod cost at the regular `—`
            // path (handled in `Tree.tsx` via the `node.type === "pod"`
            // branch) without aggregating an unknown into the
            // parent pool / service / global stats.
            hourlyCostUnknown:
              isBillable(replica) &&
              typeof replica.current_hourly_cost_usd !== "number",
            desired: 0,
          };
          podNodes.push({
            // When the controller hasn't surfaced individual `Pod`
            // rows for a replica yet (common during the first few
            // seconds after a Sky replica registers, and the steady
            // state for backends that don't expose pod-level
            // metadata at all), we still want the row to be
            // focusable. `Workloads.tsx` has a replica-id fallback
            // in its detail resolver — feed it the replica id so
            // that path is reachable. `key` keeps the `pod:` vs
            // `replica:` prefix distinction so the two id spaces
            // don't collide in the expansion / selection sets.
            key: pod ? `pod:${pod.id}` : `replica:${replica.id}`,
            level: 3,
            type: "pod",
            id: pod ? pod.id : replica.id,
            label: pod
              ? `${pod.node_role}#${pod.node_index}`
              : `replica#${replica.id}`,
            sublabel: replica.external_ip
              ? `${replica.external_ip}${
                  replica.external_port ? ":" + replica.external_port : ""
                }`
              : pod?.external_ip ?? undefined,
            podChips: podSublabelChips(replica),
            service,
            pool,
            replica,
            podIndex: pod?.node_index,
            podRole: pod?.node_role,
            podInternalIp: pod?.internal_ip,
            podExternalIp: pod?.external_ip,
            podLocation: {
              cloud: replica.cloud ?? null,
              region: replica.region ?? null,
            },
            stats: podStat,
            children: [],
          });
        }
      }
      const sublabel = poolDisplaySublabel(pool);
      // Distinct set of clouds materialised on this pool's replicas.
      // Used as a compact "where are these replicas?" hint on the pool
      // row. Single-cloud pool => one Pill; mixed pool => one Pill per
      // cloud (rare but operationally meaningful).
      const poolClouds = Array.from(
        new Set(
          poolReplicas
            .map((r) => r.cloud)
            .filter((c): c is string => !!c),
        ),
      ).sort();
      poolNodes.push({
        key: `pool:${pool.id}`,
        level: 2,
        type: "pool",
        id: pool.id,
        label: poolDisplayLabel(pool),
        sublabel: sublabel.text,
        poolChips: sublabel.chips,
        service,
        pool,
        stats: ps,
        poolClouds,
        children: podNodes,
      });
    }

    return {
      key: `service:${service.id}`,
      level: 1,
      type: "service",
      id: service.id,
      label: labels.service(service),
      sublabel: service.description ?? undefined,
      service,
      stats: serviceStats,
      children: poolNodes,
    };
  });

  // Virtual root that owns every service. Its stats are the aggregate of
  // the children's stats so the row gives a one-glance system summary.
  const globalStats = emptyStats();
  for (const sn of serviceNodes) addStats(globalStats, sn.stats);
  const serviceCount = serviceNodes.length;
  const globalNode: TreeNode = {
    key: GLOBAL_TREE_KEY,
    level: 0,
    type: "global",
    id: null,
    label: "BackStage",
    sublabel: `envoy data plane · ${serviceCount} service${
      serviceCount === 1 ? "" : "s"
    }`,
    stats: globalStats,
    children: serviceNodes,
  };
  return [globalNode];
}

/** All ancestor keys for `key` so we can auto-expand to a focus target. */
export function ancestorKeys(roots: TreeNode[], targetKey: string): string[] {
  const acc: string[] = [];
  const dfs = (node: TreeNode, trail: string[]): boolean => {
    if (node.key === targetKey) {
      acc.push(...trail);
      return true;
    }
    for (const c of node.children) {
      if (dfs(c, [...trail, node.key])) return true;
    }
    return false;
  };
  for (const r of roots) dfs(r, []);
  return acc;
}
